import { action, makeObservable, observable, reaction, runInAction } from 'mobx';
import { RootStore } from 'store/rootStore';
import { formUtils } from '@insurancemenu/imenu-components-lib';
import { FormObjectFromJSON } from '@insurancemenu/imenu-components-lib/dist/types';
import {
  DDARAdditionalContact,
  DDARCustomUser,
  DDIAAffiliateSubsidiary,
  DDIAAnotherContract,
  DDIANotActivelyAtWorkEE,
  FormInstanceType,
  ZywaveBravoHealthImqDoctor,
  ZywaveBravoHealthImqMedication,
  ZywaveMemorialHermannCompensationEE,
  ZywaveMemorialHermannExemptEE,
} from 'types/formService';
import {
  CustomFieldControl,
  DynamicCustomFieldsDefaultType,
  DynamicFormController,
} from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { requestEmployerFormInstance, requestSetAnswers } from 'api/operations/formService';

export interface WebFormCustomFields extends DynamicCustomFieldsDefaultType {
  authorizedUsers: CustomFieldControl<DDARCustomUser[]>;
  textWithCompanyName: CustomFieldControl<string>;
  termsAndConditions: CustomFieldControl<string>;
  affiliateSubsidiary: CustomFieldControl<DDIAAffiliateSubsidiary[]>;
  anotherContract: CustomFieldControl<DDIAAnotherContract[]>;
  notActivelyAtWorkEE: CustomFieldControl<DDIANotActivelyAtWorkEE[]>;
  memorialHermannExemptEE: CustomFieldControl<ZywaveMemorialHermannExemptEE[]>;
  memorialHermannCompensationEE: CustomFieldControl<ZywaveMemorialHermannCompensationEE[]>;
  bravoHealthImqDoctor: CustomFieldControl<ZywaveBravoHealthImqDoctor[]>;
  bravoHealthImqMedication: CustomFieldControl<ZywaveBravoHealthImqMedication[]>;
  ddarAdditionalContacts: CustomFieldControl<DDARAdditionalContact[]>;
}

export default class WebFormStore {
  rootStore: RootStore;
  formInstance: null | FormInstanceType;
  dynamicForm: null | formUtils.DynamicFormController<WebFormCustomFields>;
  loading = false;

  setLoading(loadingStatus: boolean): void {
    this.loading = loadingStatus;
  }

  createFormController(formObject: FormObjectFromJSON): void {
    this.setLoading(true);
    this.dynamicForm = new DynamicFormController<WebFormCustomFields>(formObject, this.rootStore.userStore.userRoles);
    this.setLoading(false);
  }

  async getFormInstance(formInstanceId: string): Promise<void> {
    this.setLoading(true);
    this.formInstance = null;
    this.dynamicForm = null;
    const res = await requestEmployerFormInstance(formInstanceId);
    runInAction(() => {
      this.formInstance = res.data.formInstanceById;
      this.setLoading(false);
    });
  }

  async setAnswers(complete: boolean): Promise<void> {
    if (this.formInstance && this.dynamicForm) {
      try {
        this.setLoading(true);
        const res = await requestSetAnswers({
          formInstanceId: this.formInstance.id,
          answers: {
            common: this.dynamicForm.getCanonicalAnswers(),
            custom: this.dynamicForm.getCustomFormFieldsObject(),
          },
          complete,
        });
        runInAction(() => {
          if (res.data) {
            this.formInstance = res.data.setAnswers;
          }
        });
      } finally {
        this.setLoading(false);
      }
    }
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.dynamicForm = null;
    this.formInstance = null;

    makeObservable(this, {
      dynamicForm: observable,
      loading: observable,
      formInstance: observable,
      setLoading: action.bound,
      createFormController: action.bound,
      setAnswers: action.bound,
      getFormInstance: action.bound,
    });

    reaction(
      () => this.formInstance,
      (formInstance) => {
        return formInstance && this.createFormController(formInstance.form);
      },
    );

    reaction(
      () => this.dynamicForm,
      (dynamicForm) => {
        return (
          dynamicForm &&
          dynamicForm.populateFields(this.formInstance!.answers.common as unknown as Record<string, string>)
        );
      },
    );

    reaction(
      () => this.dynamicForm,
      (dynamicForm) => {
        return (
          dynamicForm &&
          dynamicForm.populateCustomFields(this.formInstance!.answers.custom as unknown as Record<string, string>)
        );
      },
    );
  }
}
